import { useEffect, useRef } from 'react';
import { OfertaData, ItemOfertaData } from '../../types';

interface UsePriceCalculationProps {
  currentItem: ItemOfertaData | undefined;
  itemIndex: number;
  setDataOferta: React.Dispatch<React.SetStateAction<OfertaData>>;
}

/**
 * Hook personalizado para calcular automáticamente el precio neto y total
 * basado en precio bruto, descuento y cantidad
 */
export const usePriceCalculation = ({
  currentItem,
  itemIndex,
  setDataOferta,
}: UsePriceCalculationProps) => {
  const grossAmount = currentItem?.precio_bruto ?? 0;
  const discount = currentItem?.descuento ?? 0;
  const scheduleLineOrderQuantity = currentItem?.cantidad ?? 0;

  // Ref para evitar recálculos innecesarios
  const prevValuesRef = useRef({
    grossAmount: 0,
    discount: 0,
    scheduleLineOrderQuantity: 0,
  });

  // Calcular precio neto automáticamente
  useEffect(() => {
    if (!currentItem) return;

    // Verificar si los valores han cambiado realmente
    const hasChanged =
      prevValuesRef.current.grossAmount !== grossAmount ||
      prevValuesRef.current.discount !== discount ||
      prevValuesRef.current.scheduleLineOrderQuantity !== scheduleLineOrderQuantity;

    if (!hasChanged) return;

    // Actualizar la referencia
    prevValuesRef.current = {
      grossAmount,
      discount,
      scheduleLineOrderQuantity,
    };

    const precioNetoCalculado = Number(
      (grossAmount * (1 - discount / 100)).toFixed(2),
    );
    const netAmountCalculado = precioNetoCalculado * scheduleLineOrderQuantity;

    setDataOferta((prev) => ({
      ...prev,
      items_oferta: prev.items_oferta.map((item, index) =>
        index === itemIndex
          ? {
            ...item,
            precio_neto: precioNetoCalculado,
            precio_total: netAmountCalculado,
          }
          : item,
      ),
    }));
  }, [grossAmount, discount, scheduleLineOrderQuantity, itemIndex, setDataOferta, currentItem]);

  return { prevValuesRef };
};

